import React from "react";
import { Button } from "antd";
import { CloseOutlined, UserOutlined, ClockCircleOutlined, TeamOutlined, TableOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Reservation } from "../../../../utils/api/reservationsAPI";
import "./LiveReservationAlert.scss";

interface Props {
  reservations: Reservation[];
  onClose: () => void;
  onView: (r: Reservation) => void;
}

const sourceLabel = (source: string) => {
  switch (source) {
    case "website": return "🌐 Web";
    case "widget":  return "🔗 Widget";
    case "phone":   return "📞 Telefon";
    case "voice":   return "🎙️ Voice";
    default:        return "🖥️ Interno";
  }
};

const LiveReservationAlert: React.FC<Props> = ({ reservations, onClose, onView }) => {
  const { t } = useTranslation();

  const latest = reservations[reservations.length - 1];
  const count = reservations.length;
  const isMultiple = count > 1;

  return (
    <div className="lra-overlay" onClick={onClose}>
      <div className="lra-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="lra-header">
          <div className="lra-bell">
            <span className="lra-bell__icon">🔔</span>
          </div>
          <div className="lra-header__text">
            <div className="lra-header__title">
              {isMultiple
                ? t("{{count}} nove rezervacije!", { count })
                : t("Nova rezervacija!")}
            </div>
            <div className="lra-header__sub">
              {isMultiple
                ? t("Stiglo je više rezervacija dok ste bili odsutni")
                : sourceLabel(latest.source)}
            </div>
          </div>
          <button className="lra-close" onClick={onClose}>
            <CloseOutlined />
          </button>
        </div>

        {/* Single reservation detail */}
        {!isMultiple && (
          <div className="lra-detail">
            <div className="lra-detail__name">{latest.guestName}</div>
            <div className="lra-detail__chips">
              <span className="lra-chip">
                <ClockCircleOutlined /> {latest.time}
              </span>
              <span className="lra-chip">
                <TeamOutlined /> {latest.numberOfGuests} {latest.numberOfGuests === 1 ? t("gost") : t("gostiju")}
              </span>
              {(latest.tableName || (latest.tableNames && latest.tableNames.length > 0)) && (
                <span className="lra-chip">
                  <TableOutlined /> {latest.tableNames?.join(", ") || latest.tableName}
                </span>
              )}
              {latest.note && (
                <span className="lra-chip lra-chip--note" title={latest.note}>
                  💬 {latest.note.length > 40 ? latest.note.slice(0, 40) + "…" : latest.note}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Multiple reservations list */}
        {isMultiple && (
          <div className="lra-list">
            {reservations.map((r) => (
              <div key={r._id} className="lra-list__item" onClick={() => onView(r)}>
                <div className="lra-list__item-name">
                  <UserOutlined /> {r.guestName}
                </div>
                <div className="lra-list__item-meta">
                  <ClockCircleOutlined /> {r.time} &nbsp;·&nbsp;
                  <TeamOutlined /> {r.numberOfGuests} {r.numberOfGuests === 1 ? t("gost") : t("gostiju")}
                  {(r.tableName || (r.tableNames && r.tableNames.length > 0)) && (
                    <> &nbsp;·&nbsp; <TableOutlined /> {r.tableNames?.join(", ") || r.tableName}</>
                  )}
                </div>
                <div className="lra-list__item-source">{sourceLabel(r.source)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="lra-actions">
          {!isMultiple && (
            <Button
              type="primary"
              size="large"
              className="lra-btn-view"
              onClick={() => { onView(latest); onClose(); }}
            >
              {t("Pogledaj rezervaciju")}
            </Button>
          )}
          <Button size="large" className="lra-btn-dismiss" onClick={onClose}>
            {t("Zatvori")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LiveReservationAlert;
